import { ZodError } from 'zod'

export type DatabaseErrorCode =
  | 'validation'
  | 'not_found'
  | 'conflict'
  | 'relationship'
  | 'protected_record'
  | 'invalid_state'
  | 'quota'
  | 'migration'
  | 'unavailable'
  | 'unexpected'

const defaultMessages: Record<DatabaseErrorCode, string> = {
  validation: 'Alguns dados são inválidos. Revise as informações e tente novamente.',
  not_found: 'O registro solicitado não foi encontrado.',
  conflict: 'Já existe um registro com este código ou número.',
  relationship: 'Não foi possível salvar porque um vínculo obrigatório é inválido.',
  protected_record: 'Este registro possui vínculos e não pode ser apagado.',
  invalid_state: 'Esta operação não é permitida no estado atual do registro.',
  quota: 'O armazenamento local está sem espaço suficiente.',
  migration: 'Não foi possível atualizar o banco local com segurança.',
  unavailable: 'O armazenamento local não está disponível neste navegador.',
  unexpected: 'Não foi possível concluir a operação no banco local.',
}

export class DatabaseOperationError extends Error {
  readonly code: DatabaseErrorCode
  readonly userMessage: string

  constructor(
    code: DatabaseErrorCode,
    technicalMessage: string,
    userMessage = defaultMessages[code],
  ) {
    super(technicalMessage)
    this.name = 'DatabaseOperationError'
    this.code = code
    this.userMessage = userMessage
  }
}

export function toDatabaseOperationError(error: unknown, context: string): DatabaseOperationError {
  if (error instanceof DatabaseOperationError) return error
  if (error instanceof ZodError) {
    return new DatabaseOperationError('validation', `${context}: ${error.message}`)
  }
  if (error instanceof DOMException) {
    if (error.name === 'ConstraintError') {
      return new DatabaseOperationError('conflict', `${context}: ${error.message}`)
    }
    if (error.name === 'QuotaExceededError') {
      return new DatabaseOperationError('quota', `${context}: ${error.message}`)
    }
    if (error.name === 'VersionError' || error.name === 'InvalidStateError') {
      return new DatabaseOperationError('migration', `${context}: ${error.message}`)
    }
  }
  if (error instanceof Error) {
    if (error.name === 'ConstraintError') {
      return new DatabaseOperationError('conflict', `${context}: ${error.message}`)
    }
    if (error.name === 'QuotaExceededError') {
      return new DatabaseOperationError('quota', `${context}: ${error.message}`)
    }
    if (error.name === 'VersionError' || error.name === 'InvalidStateError') {
      return new DatabaseOperationError('migration', `${context}: ${error.message}`)
    }
  }
  const message = error instanceof Error ? error.message : String(error)
  return new DatabaseOperationError('unexpected', `${context}: ${message}`)
}

export async function executeDatabaseOperation<T>(
  context: string,
  operation: () => Promise<T>,
): Promise<T> {
  try {
    return await operation()
  } catch (error) {
    throw toDatabaseOperationError(error, context)
  }
}
